import axios from "axios";
import {
  masterInstacne,
  refershInstance,
  ClientInstance,
  REQUEST_TIMEOUT_MS,
  getRequestTimeout,
} from "utils/axios-instance";
import { createBrowserHistory } from "history";
import * as actionTypes from "../store/actionTypes";
import { WebConfig } from "../config";

const history = createBrowserHistory({
  forceRefresh: true,
});
let axiosLoadCount = 0;
let clientLoadCount = 0;
let axiosHideTimer = null;
let clientHideTimer = null;
let refreshRequest = null;

const SESSION_STORAGE_KEYS = [
  "private_token",
  "refersh_token",
  "impersonate_access_token",
  "inperosnate",
  "inperosnateId",
  "CompanyId",
  "user_company_users",
  "two_factor",
  "two_factor_done",
  "username",
  "password",
  "azureToken",
  "LogAzure",
];

const clearSessionStorage = () => {
  SESSION_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

export const redirectToLogin = (expectedRefreshToken = null) => {
  // Do not let an old request destroy a session created while it was pending.
  if (
    expectedRefreshToken &&
    localStorage.getItem("refersh_token") !== expectedRefreshToken
  ) {
    return false;
  }
  clearSessionStorage();
  // A hard replacement is intentional: reloading the current protected route
  // starts the application with no token and can create an endless auth loop.
  window.location.replace("/login");
  return true;
};

const leaveExpiredImpersonation = () => {
  localStorage.removeItem("inperosnate");
  localStorage.removeItem("inperosnateId");
  localStorage.removeItem("impersonate_access_token");
  window.location.replace("/");
};

const isImpersonating = () =>
  Boolean(
    localStorage.getItem("inperosnate") &&
      localStorage.getItem("impersonate_access_token") &&
      localStorage.getItem("impersonate_access_token") !==
        localStorage.getItem("private_token")
  );

const rejectedRefreshError = (message) => {
  const error = new Error(message);
  error.authRefreshRejected = true;
  return error;
};

const changedSessionError = () => {
  const error = new Error("Authentication session changed");
  error.authSessionChanged = true;
  return error;
};

const refreshAccessToken = () => {
  const refreshToken = localStorage.getItem("refersh_token");
  const previousAccessToken = localStorage.getItem("private_token");
  const impersonationToken = localStorage.getItem("impersonate_access_token");
  const impersonationAliasesPrimary = Boolean(
    previousAccessToken && impersonationToken === previousAccessToken
  );
  if (!refreshToken) {
    return Promise.reject(rejectedRefreshError("Refresh token is missing"));
  }

  if (!refreshRequest) {
    refreshRequest = refershInstance
      .post(
        `${WebConfig.serviceUrls.RestApi}${WebConfig.ApiUrls.refreshToken}`,
        { refresh: refreshToken },
        { headers: { "Content-Type": "application/json" } }
      )
      .then((refreshResponse) => {
        const tokens = refreshResponse.data?.response;
        if (!tokens?.access) {
          if (
            refreshResponse.data?.status === 400 ||
            refreshResponse.data?.status === 401
          ) {
            throw rejectedRefreshError("Refresh token was rejected");
          }
          throw new Error("Token refresh is temporarily unavailable");
        }
        if (localStorage.getItem("refersh_token") !== refreshToken) {
          // Logout or a new login happened while this request was in flight.
          // Never restore or overwrite credentials from the previous session.
          throw changedSessionError();
        }
        localStorage.setItem("private_token", tokens.access);
        // Company-admin/Azure sessions use the impersonation slot as an alias
        // of the primary token. Keep that alias current, but never replace a
        // distinct token belonging to genuine impersonation.
        if (impersonationAliasesPrimary) {
          localStorage.setItem("impersonate_access_token", tokens.access);
        }
        if (tokens.refresh) {
          localStorage.setItem("refersh_token", tokens.refresh);
        }
        return tokens.access;
      })
      .catch((error) => {
        if (
          error.authRefreshRejected ||
          error.response?.status === 400 ||
          error.response?.status === 401
        ) {
          throw rejectedRefreshError(error.message || "Refresh token was rejected");
        }
        // Network, timeout, and server failures are temporary. Preserve the
        // session so an actively working user can retry when service returns.
        throw error;
      })
      .finally(() => {
        refreshRequest = null;
      });
  }

  return refreshRequest;
};

const retryWithFreshToken = (config, instance) => {
  if (config._authRetry) {
    redirectToLogin(config._authSessionToken);
    return Promise.reject(new Error("Refreshed token was rejected"));
  }

  if (instance === ClientInstance && isImpersonating()) {
    leaveExpiredImpersonation();
    return Promise.reject(new Error("Impersonated session expired"));
  }

  config._authRetry = true;
  const attemptedRefreshToken = localStorage.getItem("refersh_token");
  return refreshAccessToken().then(
    (accessToken) => {
      config._authSessionToken = localStorage.getItem("refersh_token");
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${accessToken}`,
      };
      return instance.request(config);
    },
    (error) => {
      if (error.authRefreshRejected) {
        redirectToLogin(attemptedRefreshToken);
      }
      return Promise.reject(error);
    }
  );
};

const beginLoading = (store, counterName) => {
  if (clientHideTimer) clearTimeout(clientHideTimer);
  if (axiosHideTimer) clearTimeout(axiosHideTimer);
  clientHideTimer = null;
  axiosHideTimer = null;
  if (counterName === "client") {
    clientLoadCount += 1;
  } else {
    axiosLoadCount += 1;
  }
  store.dispatch({ type: actionTypes.LoadTodoRequested });
  store.dispatch({ type: actionTypes.LoaderShow });
};

const finishLoading = (store, counterName) => {
  if (counterName === "client") {
    clientLoadCount = Math.max(0, clientLoadCount - 1);
    if (clientLoadCount === 0 && axiosLoadCount === 0) {
      if (clientHideTimer) clearTimeout(clientHideTimer);
      clientHideTimer = setTimeout(() => {
        if (clientLoadCount === 0 && axiosLoadCount === 0) {
          store.dispatch({ type: actionTypes.LoaderHide });
        }
        clientHideTimer = null;
      }, 500);
    }
  } else {
    axiosLoadCount = Math.max(0, axiosLoadCount - 1);
    if (axiosLoadCount === 0 && clientLoadCount === 0) {
      if (axiosHideTimer) clearTimeout(axiosHideTimer);
      axiosHideTimer = setTimeout(() => {
        if (axiosLoadCount === 0 && clientLoadCount === 0) {
          store.dispatch({ type: actionTypes.LoaderHide });
        }
        axiosHideTimer = null;
      }, 500);
    }
  }
};

masterInstacne.interceptors.request.use(
  (config) => {
    config.timeout = config.timeout || getRequestTimeout(config.url);
    // const userInfo = JSON.parse(localStorage.getItem("_tokenExpireDate"));
    // const expDuration = (new Date(userInfo).getTime());
    // if (expDuration < (new Date().getTime() - 1000)) {
    //     refershInstance
    //         .post(`${WEB_POINT_URL}/o/token/`, JSON_to_URLEncoded(
    //             {
    //                 client_id: process.env.CLIENT_ID,
    //                 client_secret: process.env.CLIENT_SECRET,
    //                 grant_type: process.env.GRANT_TYPE_REFRESH_TOKEN,
    //                 refresh_token: `${localStorage.getItem('refersh_token')}`
    //             }

    //         ), {
    //             headers: {
    //                 'Content-Type': 'application/x-www-form-urlencoded',
    //             },
    //         })
    //         .then(response => {
    //             localStorage.setItem('public_token', response.data.access_token)
    //             localStorage.setItem('refersh_token', response.data.refresh_token)
    //             const expDate = new Date(new Date().getTime() + +response.data.expires_in * 1000);
    //             localStorage.setItem('_tokenExpireDate', JSON.stringify(expDate));
    //             window.location.reload();
    //         }).catch(() => {
    //             localStorage.clear();
    //             history.push("/");
    //         });
    // }
    return config;
  },
  (error) => {
    Promise.reject(error);
  }
);

masterInstacne.interceptors.response.use(
  (response) => {
    // Master-data calls are anonymous and cannot determine session validity.
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const clientinterceptor = (store) => {
  ClientInstance.interceptors.request.use((config) => {
    if (!config.skipGlobalLoader) {
      beginLoading(store, "client");
    }
    if (!config.timeout || config.timeout === REQUEST_TIMEOUT_MS) {
      config.timeout = getRequestTimeout(config.url);
    }
    // if (config.url !== `${API_POINT_URL}events/inspections/widgets/?widget=average_score`) {

    // }
    config.headers = {
      ...config.headers,
      Authorization: localStorage.getItem("impersonate_access_token")
        ? `Bearer ${localStorage.getItem("impersonate_access_token")}`
        : `Bearer ${localStorage.getItem("private_token")}`,
      Company: localStorage.getItem("inperosnateId"),
      "Accept-Language": localStorage.getItem("language")
        ? localStorage.getItem("language")
        : "en",
    };
    // console.log("config" , config) localStorage.getItem("inperosnateId");
    // console.log("API_POINT_URL" , API_POINT_URL);
    return config;
  });

  ClientInstance.interceptors.response.use(
    (response) => {
      if (!response.config.skipGlobalLoader) {
        finishLoading(store, "client");
      }
      if (response?.data?.status === 401) {
        if (
          !localStorage.getItem("private_token") &&
          !localStorage.getItem("impersonate_access_token")
        ) {
          return response;
        }
        return retryWithFreshToken(response.config, ClientInstance);
      }
      return response;
    },
    (error) => {
      // console.log("error" , error.response);
      //clientLoadCount = 0;
      if (!error.config?.skipGlobalLoader) {
        finishLoading(store, "client");
      }
      if (!error.response) {
        if (error.config?.handleErrorLocally) {
          return Promise.reject(error);
        }
        store.dispatch({
          type: actionTypes.LoadTodoError,
          payload:
            error.code === "ECONNABORTED"
              ? "The request timed out. Please try again."
              : error.message || "Unable to reach the server.",
        });
        return Promise.reject(error);
      }

      if (error.response.status === 401) {
        if (
          !localStorage.getItem("private_token") &&
          !localStorage.getItem("impersonate_access_token")
        ) {
          return Promise.reject(error);
        }
        return retryWithFreshToken(error.config, ClientInstance);
      }

      if (error.config?.handleErrorLocally) {
        return Promise.reject(error);
      }

      if (error.response && error.response.status === 500) {
        setTimeout(() => {
          history.push("/500", {
            Errordata: error.response.data,
            PrevLocation: history.location,
          });
        }, 1000);
        return Promise.reject(error);
      }

      if (error.response && error.response.status !== 404) {
        store.dispatch({
          type: actionTypes.LoadTodoError,
          payload: error.response.statusText,
        });
        return Promise.reject(error);
      }
      if (error.response && error.response.status === 404) {
        store.dispatch({
          type: actionTypes.LoadTodoError,
          payload: error.response.statusText,
        });
        return Promise.reject(error);
      }
      store.dispatch({ type: actionTypes.LoadTodoError, payload: `${error}` });
      return Promise.reject(error);
    }
  );
};

const axiosinterceptor = (store) => {
  axios.interceptors.request.use((config) => {
    beginLoading(store, "axios");
    config.timeout = config.timeout || getRequestTimeout(config.url);
    if (localStorage.getItem("private_token")) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${localStorage.getItem("private_token")}`,
        "Accept-Language": localStorage.getItem("language")
          ? localStorage.getItem("language")
          : "en",
      };
      if (localStorage.getItem("CompanyId")) {
        config.headers = {
          Authorization: `Bearer ${localStorage.getItem("private_token")}`,
          "Accept-Language": localStorage.getItem("language")
            ? localStorage.getItem("language")
            : "en",
          Company: localStorage.getItem("CompanyId"),
        };
      }
    }
    return config;
  });

  axios.interceptors.response.use(
    (response) => {
      finishLoading(store, "axios");
      // The backend may represent expiry with different message shapes. Treat
      // every authenticated 401 as refreshable and let the one-retry guard
      // decide whether the refreshed session is genuinely unauthorized.
      if (response?.data?.status === 401) {
        if (!localStorage.getItem("private_token")) {
          return response;
        }
        return retryWithFreshToken(response.config, axios);
      }
      return response;
    },
    (error) => {
      finishLoading(store, "axios");
      if (!error.response) {
        store.dispatch({
          type: actionTypes.LoadTodoError,
          payload:
            error.code === "ECONNABORTED"
              ? "The request timed out. Please try again."
              : error.message || "Unable to reach the server.",
        });
        return Promise.reject(error);
      }

      if (error.response.status === 401) {
        if (!localStorage.getItem("private_token")) {
          return Promise.reject(error);
        }
        return retryWithFreshToken(error.config, axios);
      }

      if (error.response && error.response.status === 500) {
        setTimeout(() => {
          history.push("/500", {
            Errordata: error.response.data,
            PrevLocation: history.location,
          });
        }, 1000);
        return Promise.reject(error);
      }
      if (error.response && error.response.status !== 404) {
        store.dispatch({
          type: actionTypes.LoadTodoError,
          payload: error.response.statusText,
        });
        return Promise.reject(error);
      }
      if (error.response && error.response.status === 404) {
        store.dispatch({
          type: actionTypes.LoadTodoError,
          payload: error.response.statusText,
        });
        return Promise.reject(error);
      }
      store.dispatch({ type: actionTypes.LoadTodoError, payload: `${error}` });
      return Promise.reject(error);
    }
  );
};

export default {
  axiosinterceptor,
  clientinterceptor,
};
